/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import PageAccessLevel from '@linc-technologies/linced-common/enums/page-access-level';
import type PageType from '@linc-technologies/linced-common/enums/page-type';
import type ReactionType from '@linc-technologies/linced-common/enums/reaction-type';
import type Assessment from '@linc-technologies/linced-common/models/assessments/assessment';
import type GoalSubject from '@linc-technologies/linced-common/models/goals/subject';
import type Media from '@linc-technologies/linced-common/models/media';
import type PageSet from '@linc-technologies/linced-common/models/posts/page-set';
import type PostTemplate from '@linc-technologies/linced-common/models/posts/post-template';
import type TagCategory from '@linc-technologies/linced-common/models/posts/tag-category';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: posts.Page.
export default class Page extends Model {
  declare [Type]: 'posts/page';

  // Page meta
  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PageType> | 0;

  @attr('boolean', { defaultValue: false })
  declare hasAssessments: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasPosts: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasGoals: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasEffort: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasGoalsAsTags: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasOTJAssessment: boolean;

  // Page configuration
  @attr('string', { defaultValue: '' })
  declare icon: string;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare title: string;

  @belongsTo('media', { async: true, inverse: null })
  declare mediaId: AsyncBelongsTo<Media>;

  @alias('mediaId')
  declare media: AsyncBelongsTo<Media>;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @belongsTo('media', { async: true, inverse: null })
  declare caregiverMediaId: AsyncBelongsTo<Media>;

  @alias('caregiverMediaId')
  declare caregiverMedia: AsyncBelongsTo<Media>;

  @attr('string', { defaultValue: '' })
  declare caregiverDescription: string;

  @belongsTo('media', { async: true, inverse: null })
  declare learnerMediaId: AsyncBelongsTo<Media>;

  @alias('learnerMediaId')
  declare learnerMedia: AsyncBelongsTo<Media>;

  @attr('string', { defaultValue: '' })
  declare learnerDescription: string;

  @attr('number', { defaultValue: 0 })
  declare reactionType: EnumValue<typeof ReactionType> | 0;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  // Page Sets
  @hasMany('posts/page-set', { async: true, inverse: 'pages' })
  declare pageSetIds: AsyncHasMany<PageSet>;

  @alias('pageSetIds')
  declare pageSets: AsyncHasMany<PageSet>;

  // Page visibility
  @attr('boolean', { defaultValue: false })
  declare visibleInMenu: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleOnContent: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleOnStudentProfile: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToParents: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToLearners: boolean;

  // Page authorisation / permissions
  @attr('boolean', { defaultValue: false })
  declare staffCanPublish: boolean;

  @attr('boolean', { defaultValue: false })
  declare staffCanEdit: boolean;

  @attr('boolean', { defaultValue: false })
  declare caregiversCanPost: boolean;

  @attr('boolean', { defaultValue: false })
  declare learnersCanPost: boolean;

  @attr('boolean', { defaultValue: false })
  declare canComment: boolean;

  // Page magic
  @attr('boolean', { defaultValue: false })
  declare hasLearnerIcon: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasAutogroup: boolean;

  // Page content filters
  @hasMany('assessments/assessment', { async: true, inverse: null })
  declare assessmentIds: AsyncHasMany<Assessment>;

  // assessments resolves to the loaded records rather than exposing the
  // PromiseManyArray itself: touching that promise's array-like surface
  // directly (e.g. a template {{#each}}, or .filter()/.map()) is deprecated.
  @computed('assessmentIds.content.[]')
  get assessments() {
    return resolved(this.assessmentIds);
  }

  @computed('assessments.[]')
  get assessment_ids() {
    return this.assessments.map((assessment) => assessment.id);
  }

  @hasMany('goals/subject', { async: true, inverse: null })
  declare goalSubjectIds: AsyncHasMany<GoalSubject>;

  // See assessments above: resolves to the loaded records, not the
  // PromiseManyArray.
  @computed('goalSubjectIds.content.[]')
  get goalSubjects() {
    return resolved(this.goalSubjectIds);
  }

  @computed('goalSubjects.[]')
  get goalSubject_ids() {
    return this.goalSubjects.map((subject) => subject.id);
  }

  @hasMany('posts/tag-category', { async: true, inverse: null })
  declare tagCategoryIds: AsyncHasMany<TagCategory>;

  // See assessments above: resolves to the loaded records, not the
  // PromiseManyArray.
  @computed('tagCategoryIds.content.[]')
  get tagCategories() {
    return resolved(this.tagCategoryIds);
  }

  // Access level for page
  @attr('number', { defaultValue: PageAccessLevel.STANDARD })
  declare accessLevel: EnumValue<typeof PageAccessLevel> | 0;

  // Post Templates
  // Legacy (simple) templates
  @attr('string', { defaultValue: '' })
  declare postTemplate: string;

  // Full-fat templates
  @hasMany('posts/post-template', { async: true, inverse: null })
  declare staffTemplateIds: AsyncHasMany<PostTemplate>;

  @hasMany('posts/post-template', { async: true, inverse: null })
  declare learnerTemplateIds: AsyncHasMany<PostTemplate>;

  @hasMany('posts/post-template', { async: true, inverse: null })
  declare caregiverTemplateIds: AsyncHasMany<PostTemplate>;

  @attr('boolean', { defaultValue: false })
  declare staffRequireTemplate: boolean;

  @attr('boolean', { defaultValue: false })
  declare learnersRequireTemplate: boolean;

  @attr('boolean', { defaultValue: false })
  declare caregiversRequireTemplate: boolean;

  // Is this a Sickbay page
  @attr('boolean', { defaultValue: false })
  declare isSickbay: boolean;

  @attr('boolean', { defaultValue: false })
  declare isSuspensionPosts: boolean;

  @attr('boolean', { defaultValue: false })
  declare isSuspensionLetters: boolean;

  @attr('boolean', { defaultValue: false })
  declare isBehaviour: boolean;

  @attr('boolean', { defaultValue: false })
  declare isAttendance: boolean;

  // Is this a special use page
  // ie. sickbay
  @computed(
    'isSickbay',
    'isSuspensionPosts',
    'isSuspensionLetters',
    'isBehaviour',
  )
  get isSpecialUse() {
    return (
      this.isSickbay ||
      this.isSuspensionPosts ||
      this.isSuspensionLetters ||
      this.isAttendance ||
      this.isBehaviour
    );
  }

  @computed(
    'isSickbay',
    'isSuspensionPosts',
    'isSuspensionLetters',
    'isBehaviour',
    'isSpecialUse',
    'isAttendance',
  )
  get specialUseLabel() {
    if (!this.isSpecialUse) {
      return '';
    }

    const pageLabels = [
      { flag: 'isSickbay', label: 'Sickbay' },
      { flag: 'isSuspensionPosts', label: 'Suspensions Posts' },
      { flag: 'isSuspensionLetters', label: 'Suspensions Letters' },
      { flag: 'isBehaviour', label: 'Behaviour' },
      { flag: 'isAttendance', label: 'Attendance' },
    ];

    const activeLabels = pageLabels
      .filter((pageLabel) => this.get(pageLabel.flag))
      .map((pageLabel) => pageLabel.label);

    return activeLabels.join(', ') || '';
  }
}
